'use client';

import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import { HubNewsTagCategoryItem, HubNewsTagSelection } from '@/types/hubs';
import IconAdaptive from '@/components/IconAdaptive';
import SearchResultCounter from '@/components/SearchResultCounter';
import SectionTitle from '@/components/SectionTitle';
import { getPanelVisibleTags } from '@/(routers)/hubs/cases/components/hubNewsTags';
import HubNewsCopyUrlUnit from '@/(routers)/hubs/cases/components/unit/HubNewsCopyUrlUnit';
import HubNewsSearchPillUnit from '@/(routers)/hubs/cases/components/unit/HubNewsSearchPillUnit';
import HubNewsSelectedTagUnit from '@/(routers)/hubs/cases/components/unit/HubNewsSelectedTagUnit';

export interface HubNewsSearchProps {
    categories: HubNewsTagCategoryItem[];
    selection: HubNewsTagSelection;
    tagCounts: Record<string, number>;
    baseTagCounts: Record<string, number>;
    hasTaggedNews: boolean;
    resultCount: number;
    selectedCount: number;
    copyUrl: string;
    onToggleTag: (categoryCd: string, tagCd: string) => void;
    onClear: () => void;
    onOpenDetail: () => void;
}

export default function HubNewsSearch({
    categories,
    selection,
    tagCounts,
    baseTagCounts,
    hasTaggedNews,
    resultCount,
    selectedCount,
    copyUrl,
    onToggleTag,
    onClear,
    onOpenDetail,
}: HubNewsSearchProps) {
    const primaryCategories = categories.filter((category) => category.is_primary);
    const selectedTags = categories.flatMap((category) =>
        category.tags
            .filter((tag) => (selection[category.cd] ?? []).includes(tag.cd))
            .map((tag) => ({ categoryCd: category.cd, tag })),
    );

    const getPrimaryVisibleTags = (category: HubNewsTagCategoryItem) => {
        const selectedTagCds = selection[category.cd] ?? [];
        return category.tags.filter((tag) => (baseTagCounts[tag.cd] ?? 0) > 0 || selectedTagCds.includes(tag.cd));
    };

    const hasSelectableTags = categories.some(
        (category) => getPanelVisibleTags(category, selection, hasTaggedNews).length > 0,
    );

    const hasPrimaryTags = primaryCategories.some((category) => getPrimaryVisibleTags(category).length > 0);

    return (
        <div className="flex flex-col w-full">
            <SectionTitle
                title="Search."
                subTitle={hasSelectableTags ? '条件で事例を絞り込む（複数選べます）' : 'すべての事例を表示しています'}
                action={
                    selectedCount > 0 ? (
                        <HubNewsCopyUrlUnit
                            copyUrl={copyUrl}
                            label="この条件のURLをコピー"
                            toastMessage="この条件のURLをコピーしました"
                            size="compact"
                        />
                    ) : undefined
                }
            />

            {hasPrimaryTags && (
                <div className="hidden md:flex flex-col w-full gap-3 pt-4">
                    {primaryCategories.map((category) => {
                        const selectedTagCds = selection[category.cd] ?? [];
                        const visibleTags = getPrimaryVisibleTags(category);
                        if (visibleTags.length === 0) return null;

                        return (
                            <div
                                key={category.cd}
                                className="flex flex-col md:flex-row md:items-start w-full gap-1 md:gap-4"
                            >
                                <div className="font-notojp font-bold text-xs text-color-base md:w-[132px] md:shrink-0 md:pt-2">
                                    {category.name}
                                </div>
                                <div className="flex flex-wrap gap-2 overflow-x-auto md:overflow-visible">
                                    {visibleTags.map((tag) => (
                                        <HubNewsSearchPillUnit
                                            key={tag.cd}
                                            label={tag.name}
                                            count={tagCounts[tag.cd] ?? 0}
                                            isSelected={selectedTagCds.includes(tag.cd)}
                                            onClick={() => onToggleTag(category.cd, tag.cd)}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div
                className={`flex flex-col md:flex-row md:items-center w-full gap-3 md:gap-4 pt-1 mt-4 ${
                    hasSelectableTags ? 'md:pt-5 md:border-t md:border-color-co-gray-light' : ''
                }`}
            >
                {hasSelectableTags && (
                    <div className="flex flex-wrap items-center w-full md:w-auto md:shrink-0 gap-2">
                        <button
                            type="button"
                            className="flex flex-grow md:flex-grow-0 max-w-[200px] md:max-w-none justify-center items-center gap-2 h-9 px-5 rounded-full border-[1.5px] border-color-base bg-color-white-base text-color-base font-notojp font-bold text-xs hover:bg-color-base hover:text-color-white-base transition-colors duration-150"
                            onClick={onOpenDetail}
                        >
                            <IconAdaptive
                                Icon={SlidersHorizontal}
                                strokeWidth={2.2}
                                baseSize={15}
                                breakpoints={{ md: 15 }}
                            />
                            <span className="md:hidden">条件を選択</span>
                            <span className="hidden md:inline">さらに条件を選択</span>
                        </button>
                        {selectedCount > 0 && (
                            <button
                                type="button"
                                className="flex shrink-0 justify-center items-center gap-1.5 h-9 px-4 rounded-full border border-color-input-border bg-color-white-base text-color-co-gray-dark font-notojp font-bold text-xs hover:border-color-service-selected hover:text-color-service-selected transition-colors duration-150"
                                onClick={onClear}
                            >
                                <IconAdaptive
                                    Icon={RotateCcw}
                                    strokeWidth={2.2}
                                    baseSize={14}
                                    breakpoints={{ md: 14 }}
                                />
                                条件をクリア
                            </button>
                        )}
                    </div>
                )}
                <div className="flex flex-grow flex-wrap items-center gap-2">
                    {selectedTags.map(({ categoryCd, tag }) => (
                        <HubNewsSelectedTagUnit
                            key={tag.cd}
                            label={tag.name}
                            onRemove={() => onToggleTag(categoryCd, tag.cd)}
                        />
                    ))}
                </div>
                <div className="flex justify-start md:justify-end w-full md:w-auto md:shrink-0">
                    <SearchResultCounter prefixLabel="事例" suffixLabel="件" allCount={resultCount} />
                </div>
            </div>
        </div>
    );
}
